import type { ProductDto, UpsertFnbItemInput, UpsertServiceInput } from '@telestore/contracts';
import type { TranslationKey } from '@telestore/i18n';
import { pickText } from '@telestore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAmount, toDecimal } from '@telestore/shared';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { Button, ErrorBox, Field, Input, Panel, Spinner } from '../components/ui.js';

/**
 * The per-vertical half of a product form (§4: base product + extension).
 *
 * Rendered in place of the retail variant grid, never alongside it — a coffee
 * has no size × colour matrix and a haircut has no stock count. Saved by its
 * own endpoint so the base product save stays one call for every vertical.
 *
 * Both panels only appear for an existing product: the extension row hangs off
 * a product id, so there is nothing to attach a modifier group to until the
 * product has been created.
 */

export function ProductVerticalPanel({
  vertical,
  product,
}: {
  vertical: 'retail' | 'fnb' | 'services';
  product: ProductDto;
}) {
  if (vertical === 'fnb') return <FnbPanel product={product} />;
  if (vertical === 'services') return <ServicePanel product={product} />;
  return null;
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const WEEKDAY_KEYS = [
  'common.sun',
  'common.mon',
  'common.tue',
  'common.wed',
  'common.thu',
  'common.fri',
  'common.sat',
] as const;

function FnbPanel({ product }: { product: ProductDto }) {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['modifier-groups', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.modifierGroups(shopId!),
  });

  const existing = product.fnb;
  const [prepMinutes, setPrepMinutes] = useState(String(existing?.prepMinutes ?? 15));
  const [attached, setAttached] = useState<string[]>(
    existing?.modifierGroups.map((group) => group.id) ?? [],
  );
  /**
   * §4's per-item selling hours. One window per weekday, empty meaning "all
   * day" — a breakfast item sells 06:00–10:30 and nothing else needs saying.
   */
  const [hours, setHours] = useState<Record<number, { open: string; close: string } | null>>(
    () => {
      const initial: Record<number, { open: string; close: string } | null> = {};
      for (const weekday of WEEKDAYS) initial[weekday] = null;
      return initial;
    },
  );

  const save = useMutation({
    mutationFn: () => {
      const body: UpsertFnbItemInput = {
        isSoldOut: existing?.isSoldOut ?? false,
        prepMinutes: Math.max(0, Number(prepMinutes) || 0),
        sellingHours: WEEKDAYS.flatMap((weekday) => {
          const entry = hours[weekday];
          return entry ? [{ weekday, open: entry.open, close: entry.close }] : [];
        }),
        /** Order matters: "Size" first on a coffee, last on a pizza (§4). */
        modifierGroupIds: attached,
      };
      return api.saveFnbItem(shopId!, product.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', shopId] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <Panel title={t('fnb.chooseOptions')}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('fnb.prepMinutes')}>
          <Input
            type="number"
            min={0}
            value={prepMinutes}
            onChange={(event) => setPrepMinutes(event.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <p className="mb-1 text-sm font-medium">{t('fnb.attachedItems', { count: attached.length })}</p>
        <p className="mb-2 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
          {t('fnb.modifierGroupsHint')}
        </p>

        {groups.isPending ? <Spinner /> : null}

        <div className="flex flex-wrap gap-2">
          {groups.data?.groups.map((group) => {
            const on = attached.includes(group.id);
            return (
              <button
                key={group.id}
                type="button"
                className="rounded-full px-3 py-1.5 text-xs font-medium"
                style={{
                  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-line)'}`,
                  background: on ? 'var(--color-accent)' : 'transparent',
                  color: on ? 'var(--color-accent-contrast)' : 'var(--color-ink)',
                }}
                onClick={() =>
                  setAttached((current) =>
                    on ? current.filter((id) => id !== group.id) : [...current, group.id],
                  )
                }
              >
                {pickText(group.name, locale)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <p className="mb-1 text-sm font-medium">{t('fnb.sellingHours')}</p>
        <p className="mb-2 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
          {t('fnb.soldOutHint')}
        </p>
        <WeekdayHours hours={hours} onChange={setHours} t={t} />
      </div>

      {error ? <div className="mt-3"><ErrorBox message={error} /></div> : null}

      <Button className="mt-4" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? t('common.loading') : t('common.save')}
      </Button>
    </Panel>
  );
}

function ServicePanel({ product }: { product: ProductDto }) {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const resources = useQuery({
    queryKey: ['resources', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.resources(shopId!),
  });

  const shop = useQuery({
    queryKey: ['shop', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.shop(shopId!),
    staleTime: 5 * 60_000,
  });
  const currency = shop.data?.shop.currency ?? 'USD';

  const existing = product.service;
  const [duration, setDuration] = useState(String(existing?.durationMinutes ?? 60));
  const [buffer, setBuffer] = useState(String(existing?.bufferMinutes ?? 0));
  const [deposit, setDeposit] = useState(
    existing?.depositAmount === null || existing?.depositAmount === undefined
      ? ''
      : String(toDecimal({ amount: existing.depositAmount, currency })),
  );
  const [allowed, setAllowed] = useState<string[]>(
    existing?.resources.map((resource) => resource.id) ?? [],
  );

  const save = useMutation({
    mutationFn: () => {
      const body: UpsertServiceInput = {
        durationMinutes: Math.max(5, Number(duration) || 60),
        bufferMinutes: Math.max(0, Number(buffer) || 0),
        /**
         * Empty means no deposit — booked on trust (§4). Not zero: zero would
         * be a deposit of nothing, which would still start the hold clock.
         */
        depositAmount: deposit.trim() === '' ? null : safeAmount(deposit, currency),
        /** Empty means anyone may perform it — the one-chair barber's default. */
        resourceIds: allowed,
      };
      return api.saveService(shopId!, product.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', shopId] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <Panel title={t('resources.serviceSettings')}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('resources.durationMinutes')}>
          <Input
            type="number"
            min={5}
            value={duration}
            onChange={(event) => setDuration(event.target.value)}
          />
        </Field>
        <Field label={t('resources.bufferMinutes')} hint={t('common.optional')}>
          <Input
            type="number"
            min={0}
            value={buffer}
            onChange={(event) => setBuffer(event.target.value)}
          />
        </Field>
        <Field label={t('resources.depositAmount')} hint={t('resources.noDeposit')}>
          <Input
            inputMode="decimal"
            placeholder="—"
            value={deposit}
            onChange={(event) => setDeposit(event.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <p className="mb-1 text-sm font-medium">{t('resources.whoCanDoIt')}</p>
        <p className="mb-2 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
          {t('resources.anyone')}
        </p>

        {resources.isPending ? <Spinner /> : null}

        <div className="flex flex-wrap gap-2">
          {resources.data?.resources
            .filter((resource) => resource.isActive)
            .map((resource) => {
              const on = allowed.includes(resource.id);
              return (
                <button
                  key={resource.id}
                  type="button"
                  className="rounded-full px-3 py-1.5 text-xs font-medium"
                  style={{
                    border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-line)'}`,
                    background: on ? 'var(--color-accent)' : 'transparent',
                    color: on ? 'var(--color-accent-contrast)' : 'var(--color-ink)',
                  }}
                  onClick={() =>
                    setAllowed((current) =>
                      on ? current.filter((id) => id !== resource.id) : [...current, resource.id],
                    )
                  }
                >
                  {resource.name}
                </button>
              );
            })}
        </div>
      </div>

      {error ? <div className="mt-3"><ErrorBox message={error} /></div> : null}

      <Button className="mt-4" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? t('common.loading') : t('common.save')}
      </Button>
    </Panel>
  );
}

/** `parseAmount` throws on junk; a half-typed field is not an error here. */
function safeAmount(input: string, currency: 'USD' | 'KHR'): number {
  try {
    return parseAmount(input, currency).amount;
  } catch {
    return 0;
  }
}

function WeekdayHours({
  hours,
  onChange,
  t,
}: {
  hours: Record<number, { open: string; close: string } | null>;
  onChange: (
    next: Record<number, { open: string; close: string } | null>,
  ) => void;
  t: (key: TranslationKey) => string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {WEEKDAYS.map((weekday) => {
        const entry = hours[weekday];
        return (
          <div key={weekday} className="flex items-center gap-2">
            <button
              type="button"
              className="w-12 shrink-0 rounded-md py-1.5 text-xs font-medium"
              style={{
                border: `1px solid ${entry ? 'var(--color-accent)' : 'var(--color-line)'}`,
                background: entry ? 'var(--color-accent)' : 'transparent',
                color: entry ? 'var(--color-accent-contrast)' : 'var(--color-ink-soft)',
              }}
              onClick={() =>
                onChange({
                  ...hours,
                  [weekday]: entry ? null : { open: '06:00', close: '10:30' },
                })
              }
            >
              {t(WEEKDAY_KEYS[weekday])}
            </button>

            {entry ? (
              <>
                <Input
                  type="time"
                  value={entry.open}
                  onChange={(event) =>
                    onChange({ ...hours, [weekday]: { ...entry, open: event.target.value } })
                  }
                />
                <Input
                  type="time"
                  value={entry.close}
                  onChange={(event) =>
                    onChange({ ...hours, [weekday]: { ...entry, close: event.target.value } })
                  }
                />
              </>
            ) : (
              <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                {t('fnb.sellingHoursAllDay')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
